import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-gradient-to-b from-[#231a15] to-[#120e0c] relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="text-center mb-14 md:mb-24"
          >
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
              className="text-4xl md:text-7xl font-light mb-8 md:mb-12 leading-none tracking-tight text-amber-50"
            >
              Bizimle
              <br />
              <span className="font-normal text-amber-300/80">İletişime Geçin</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1.2, delay: 0.4, ease: "easeOut" }}
              className="text-base sm:text-lg md:text-2xl text-amber-100/75 mb-10 md:mb-16 max-w-3xl mx-auto leading-relaxed font-light"
            >
              Sipariş, öneri veya herhangi bir konuda bizimle iletişime geçebilirsiniz
            </motion.p>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.6, ease: "easeOut" }}
            className="max-w-2xl mx-auto"
          >
            <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8 rounded-2xl border border-amber-100/10 bg-amber-50/[0.02] p-5 md:p-10">
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                <div>
                  <label className="block text-amber-100/85 font-light mb-2 md:mb-3 text-base md:text-lg">Ad Soyad</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 md:px-6 py-3 md:py-4 bg-[#1a1411] border border-amber-100/10 rounded-lg text-amber-50 placeholder-amber-200/40 focus:outline-none focus:border-amber-200/40 transition-colors text-base md:text-lg"
                    placeholder="Adınız ve soyadınız"
                    required
                  />
                </div>
                <div>
                  <label className="block text-amber-100/85 font-light mb-2 md:mb-3 text-base md:text-lg">E-posta</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 md:px-6 py-3 md:py-4 bg-[#1a1411] border border-amber-100/10 rounded-lg text-amber-50 placeholder-amber-200/40 focus:outline-none focus:border-amber-200/40 transition-colors text-base md:text-lg"
                    placeholder="E-posta adresiniz"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-amber-100/85 font-light mb-2 md:mb-3 text-base md:text-lg">Telefon</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 md:px-6 py-3 md:py-4 bg-[#1a1411] border border-amber-100/10 rounded-lg text-amber-50 placeholder-amber-200/40 focus:outline-none focus:border-amber-200/40 transition-colors text-base md:text-lg"
                  placeholder="Telefon numaranız"
                />
              </div>
              
              <div>
                <label className="block text-amber-100/85 font-light mb-2 md:mb-3 text-base md:text-lg">Mesajınız</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full px-4 md:px-6 py-3 md:py-4 bg-[#1a1411] border border-amber-100/10 rounded-lg text-amber-50 placeholder-amber-200/40 focus:outline-none focus:border-amber-200/40 transition-colors text-base md:text-lg resize-none"
                  placeholder="Mesajınızı buraya yazın..."
                  required
                />
              </div>
              
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-amber-100 text-[#1e140f] py-3.5 md:py-4 px-8 rounded-lg font-medium text-base md:text-lg hover:bg-amber-200 transition-colors"
              >
                Mesaj Gönder
              </motion.button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
            className="mt-14 md:mt-24 text-center"
          >
            <div className="grid md:grid-cols-3 gap-10 md:gap-16">
              <div>
                <div className="text-4xl mb-4">📞</div>
                <h3 className="text-xl font-light text-amber-50 mb-2">Telefon</h3>
                <p className="text-amber-100/75 font-light">05447404153<br />02626428749</p>
              </div>
              <div>
                <div className="text-4xl mb-4">📧</div>
                <h3 className="text-xl font-light text-amber-50 mb-2">E-posta</h3>
                <p className="text-amber-100/75 font-light">info@kackarkarafirin.com</p>
              </div>
              <div>
                <div className="text-4xl mb-4">📍</div>
                <h3 className="text-xl font-light text-amber-50 mb-2">Adres</h3>
                <p className="text-amber-100/75 font-light">Arapçeşme mahallesi yeni bağdat cad. No:657/A Kaçkar Karafırın<br />Gebze/ KOCAELI</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 